import { getBooking, getBookingPax, getTicketAllocations } from '.';
import { RESALE_ENABLED } from '../../constants';

const getResolvedDisplayName = item => {
  if (item.displayName && item.displayName.length > 0) {
    return item.displayName;
  }

  return item?.name ?? '';
};

const ALLOWED = 'ALLOWED';

const getTicketTypeDisplayName = (ticketedEvent, ticketType) => {
  const eventName = getResolvedDisplayName(ticketedEvent);
  const ticketName = getResolvedDisplayName(ticketType);

  if (eventName.length > 0 && ticketName.length > 0) {
    return `${eventName} - ${ticketName}`;
  }

  if (eventName.length > 0) {
    return eventName;
  }

  return ticketName;
};

/**
 * Process ticket details for a passenger
 * @param {Object} ticket - Ticket data
 * @param {Object|undefined} bookingTicket - Booking ticket data
 * @param {Object} cur - Current passenger
 * @param {Number} bookingPackageId - PackageId
 * @returns {Object} Processed ticket details
 */
const processTicketDetails = (ticket, bookingTicket, cur, bookingPackageId) => {
  const {
    classId,
    id,
    ticketHolder: { dateOfBirth, email, firstName, gender, lastName, phone },
    ticketType,
    ticketedEvent,
    status,
    totalTicketPrice,
  } = ticket;

  const packageConfig = ticketType?.packageConfigurations?.find(
    config => config?.packageId === bookingPackageId,
  );

  const ticketListedForResale = bookingTicket?.resale?.code === RESALE_ENABLED;

  const isTransferable =
    packageConfig?.transferable === ALLOWED && !ticketListedForResale;

  return {
    classId,
    dateOfBirth,
    email,
    first_name: firstName,
    gender,
    id,
    isTransferable,
    last_name: lastName,
    lead_pax: cur.lead_pax,
    telephone_number: phone,
    ticketedEvent,
    ticket_type: getTicketTypeDisplayName(ticketedEvent, ticketType),
    ticketStatus: status,
    ticketValue: totalTicketPrice,
    paxId: parseInt(cur.id, 10),
  };
};

/**
 * Get passenger details from booking state
 * @param {Object} state - Redux state
 * @param {String} bookingRef - Booking reference
 * @returns {Array} Passenger details
 */
const getPassengerDetails = (state, bookingRef) => {
  const pax = getBookingPax(state, bookingRef);
  const tickets = getTicketAllocations(state, bookingRef);
  const booking = getBooking(state, bookingRef);
  const bookingPackageId = booking?.package?.id;

  return pax
    .filter(({ cancelled }) => !cancelled)
    .map(
      ({
        id,
        lead_pax,
        personal_details: { first_name, last_name, telephone_number },
      }) => ({
        first_name,
        id,
        last_name,
        lead_pax,
        telephone_number,
      }),
    )
    .reduce((agg, cur) => {
      const paxTickets =
        tickets?.filter(
          ({ paxId: ticketPaxId }) => ticketPaxId === parseInt(cur.id, 10),
        ) ?? [];

      if (paxTickets.length > 0) {
        const ticketsDetails = paxTickets.map(ticket => {
          const bookingTicket = booking?.tickets?.find(
            bt => bt.id === ticket.id,
          );

          return processTicketDetails(
            ticket,
            bookingTicket,
            cur,
            bookingPackageId,
          );
        });

        return [...agg, ...ticketsDetails];
      }

      return [...agg, cur];
    }, []);
};

export default getPassengerDetails;
